'use client';

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Zap, Euro, Calculator, ChevronRight, Info } from 'lucide-react';
import { Slider } from './Slider';
import { CountUpDisplay } from '../atomic/CountUpDisplay';

interface CitizenParticipationCalculatorMobileProps {
  totalYearlyProductionKWh: number;
  gemeindeName: string;
  className?: string;
}

const CitizenParticipationCalculatorMobile: React.FC<CitizenParticipationCalculatorMobileProps> = ({
  totalYearlyProductionKWh,
  gemeindeName,
  className = ''
}) => {
  const [participationRate, setParticipationRate] = useState(10);
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Calculations
  const participatingProductionKWh = (totalYearlyProductionKWh * participationRate) / 100;
  const averageHouseholdConsumption = 3500;
  const householdsSupplied = Math.round(participatingProductionKWh / averageHouseholdConsumption);
  const co2SavedTons = Math.round((participatingProductionKWh * 0.485) / 1000);
  const yearlyRevenue = Math.round(participatingProductionKWh * 0.04);
  
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)} Mio.`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(0)}k`;
    }
    return num.toLocaleString('de-AT');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-2xl shadow-lg overflow-hidden ${className}`}
    >
      {/* Header - Always visible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-green-100 rounded-lg">
            <Users className="w-5 h-5 text-green-600" />
          </div>
          <div className="text-left">
            <h3 className="font-bold text-gray-900">Bürgerbeteiligung</h3>
            <p className="text-xs text-gray-600">Potenzial-Rechner</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-lg font-bold text-green-600">{participationRate}%</span>
          <ChevronRight 
            className={`w-5 h-5 text-gray-400 transition-transform ${
              isExpanded ? 'rotate-90' : ''
            }`}
          />
        </div>
      </button>

      {/* Expandable Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="px-4 pb-4"
          >
            {/* Slider */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="text-sm font-medium text-gray-700">
                  Beteiligungsrate
                </label>
                <span className="text-sm text-gray-500">
                  {participationRate}% der Anlagen
                </span>
              </div>
              <Slider
                min={0}
                max={100}
                step={5}
                value={participationRate}
                onChange={setParticipationRate}
                color="#10B981"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>0%</span>
                <span>50%</span>
                <span>100%</span>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {/* Households */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-blue-50 rounded-xl p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-gray-600">Haushalte</span>
                </div>
                <CountUpDisplay
                  value={householdsSupplied}
                  className="text-xl font-bold text-gray-900"
                  duration={1000}
                />
                <p className="text-xs text-gray-500 mt-1">versorgt</p>
              </motion.div>

              {/* CO2 Savings */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-green-50 rounded-xl p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-gray-600">CO₂</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {formatNumber(co2SavedTons)}
                </div>
                <p className="text-xs text-gray-500 mt-1">Tonnen gespart</p>
              </motion.div>

              {/* Energy */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-yellow-50 rounded-xl p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4 text-yellow-600" />
                  <span className="text-xs text-gray-600">Energie</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {formatNumber(Math.round(participatingProductionKWh / 1000))}
                </div>
                <p className="text-xs text-gray-500 mt-1">MWh/Jahr</p>
              </motion.div>

              {/* Revenue */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="bg-purple-50 rounded-xl p-3"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Euro className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-gray-600">Erlöse</span>
                </div>
                <div className="text-xl font-bold text-gray-900">
                  €{formatNumber(yearlyRevenue)}
                </div>
                <p className="text-xs text-gray-500 mt-1">pro Jahr</p>
              </motion.div>
            </div>

            {/* Info Box */}
            <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-3 border border-green-100">
              <div className="flex gap-2">
                <Info className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs text-gray-700">
                    <span className="font-semibold">{gemeindeName}</span> könnte bei {participationRate}% 
                    Bürgerbeteiligung <span className="font-semibold">{householdsSupplied.toLocaleString('de-AT')}</span> Haushalte 
                    mit sauberer Energie versorgen und dabei <span className="font-semibold">€{formatNumber(yearlyRevenue)}</span> jährlich 
                    für die Gemeinde erwirtschaften.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full mt-4 bg-gradient-to-r from-green-600 to-green-700 text-white font-bold py-3 px-4 rounded-xl shadow-lg flex items-center justify-center gap-2"
            >
              <Calculator className="w-4 h-4" />
              Detaillierte Berechnung anfordern
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default CitizenParticipationCalculatorMobile;